import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';

function GeneratedRotaPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { generatedRota: initialRota, selectedDates, selectedAlgorithm } = location.state || {};
  const [rota, setRota] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [rotaName, setRotaName] = useState('');
  const [showAssignmentCounts, setShowAssignmentCounts] = useState(false);
  const [assignmentCounts, setAssignmentCounts] = useState({});
  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const { teamMembers, saveRota } = useTeam();
  const { user } = useAuth();

  const positions = ['Jimmy Jib', 'Selatan', 'Utara', 'Switcher', 'Title 1', 'Title 2', 'Animasi', 'Foto', 'Socmed'];
  const eventOrder = ['IBR1', 'IBR2', 'IBR3', 'IBR4', 'NOW', 'WBI', 'Wedding', 'Special Events'];

  useEffect(() => {
    if (initialRota) {
      // Ensure all positions exist in the rota, including Socmed
      const updatedRota = { ...initialRota };
      Object.keys(updatedRota).forEach(eventKey => {
        if (!updatedRota[eventKey]['Socmed']) {
          updatedRota[eventKey]['Socmed'] = 'Unassigned';
        }
      });
      setRota(updatedRota);
      calculateAssignmentCounts(updatedRota);
    }
  }, [initialRota]);

  const formatDateDisplay = (dateString) => {
    // Convert from yyyy-mm-dd to dd-mm-yyyy
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const groupedRota = useMemo(() => {
    if (!rota) return {};
    return Object.entries(rota).reduce((acc, [eventKey, assignments]) => {
      const [date, event] = eventKey.split(' ');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push({ event, assignments });
      return acc;
    }, {});
  }, [rota]);

  const calculateAssignmentCounts = (rotaData) => {
    const counts = {};
    Object.values(rotaData).forEach(assignments => {
      Object.values(assignments).forEach(name => {
        if (name !== 'Unassigned') {
          counts[name] = (counts[name] || 0) + 1;
        }
      });
    });
    setAssignmentCounts(counts);
  };

  const handleCellEdit = (eventKey, position, value) => {
    setRota(prevRota => {
      const newRota = {
        ...prevRota,
        [eventKey]: {
          ...prevRota[eventKey],
          [position]: value
        }
      };
      calculateAssignmentCounts(newRota);
      return newRota;
    });
  };

  const getQualifiedMembers = (position) => {
    const qualificationMap = {
      'Jimmy Jib': 'JimmyJib',
      'Selatan': 'Moving',
      'Utara': 'Moving',
      'Switcher': 'Switcher',
      'Title 1': 'Title',
      'Title 2': 'Title',
      'Animasi': 'Animasi',
      'Foto': 'Foto',
      'Socmed': 'Socmed'
    };
    const qualification = qualificationMap[position];
    return teamMembers.filter(member => member.qualifications[qualification] && member.active);
  };

  const exportToCSV = () => {
    if (!rota) return;

    // Headers
    let csvContent = 'Date,Event,' + positions.join(',') + '\n';

    // Data rows
    Object.entries(groupedRota).forEach(([date, events]) => {
      events.forEach(({ event, assignments }) => {
        const row = [
          date,
          event,
          ...positions.map(pos => assignments[pos] || 'Unassigned')
        ];
        csvContent += row.join(',') + '\n';
      });
    });

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${rotaName || 'rota'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const checkForConflicts = () => {
    const conflictList = [];
    Object.entries(rota).forEach(([eventKey, assignments]) => {
      const assignedNames = new Set();
      const conflictingNames = new Set();

      Object.entries(assignments).forEach(([position, name]) => {
        if (name !== 'Unassigned') {
          if (assignedNames.has(name)) {
            conflictingNames.add(name);
          } else {
            assignedNames.add(name);
          }
        }
      });

      if (conflictingNames.size > 0) {
        conflictList.push({
          event: eventKey,
          names: Array.from(conflictingNames)
        });
      }
    });

    setConflicts(conflictList);
    setShowConflicts(true);
    return conflictList.length === 0;
  };

  const handleSaveRota = async () => {
    if (rotaName.trim() === '') {
      alert('Please enter a name for the rota.');
      return;
    }

    const noConflicts = checkForConflicts();
    if (!noConflicts) {
      alert('There are conflicts in the rota. Please resolve them before saving.');
      return;
    }

    setIsSaving(true);
    try {
      await saveRota(rota, rotaName, user.email);
      alert('Rota saved successfully!');
      navigate('/schedules');
    } catch (error) {
      console.error('Error saving rota:', error);
      alert(`Failed to save rota. Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleAssignmentCounts = () => {
    setShowAssignmentCounts(!showAssignmentCounts);
  };

  const handleRegenerate = () => {
    navigate('/create-rota', { 
      state: { 
        selectedDates: selectedDates.map(date => new Date(date).toISOString()),
        selectedAlgorithm: selectedAlgorithm,
        isRegenerating: true 
      },
      replace: true
    });
  };

  if (!rota) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading rota data...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-10 bg-gray-50 rounded-lg shadow-md">
      <h1 className="text-4xl font-bold text-gray-800 text-center mb-8">Generated Rota</h1>
      
      {/* Rota Table */}
      <div className="overflow-x-auto mb-8 bg-white rounded-lg shadow-sm">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="p-4 text-left border-b border-gray-200 bg-gray-100 font-semibold text-gray-700 uppercase text-sm tracking-wide">
                Date
              </th>
              <th className="p-4 text-left border-b border-gray-200 bg-gray-100 font-semibold text-gray-700 uppercase text-sm tracking-wide">
                Event
              </th>
              {positions.map(position => (
                <th key={position} className="p-4 text-left border-b border-gray-200 bg-gray-100 font-semibold text-gray-700 uppercase text-sm tracking-wide">
                  {position}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedRota).map(([date, events], dateIndex) => (
              events.map(({ event, assignments }, eventIndex) => (
                <tr key={`${date}-${event}`} className={eventIndex === 0 && dateIndex > 0 ? 'border-t-4 border-gray-100' : ''}>
                  {eventIndex === 0 && (
                    <td rowSpan={events.length} className="p-4 font-bold text-lg bg-gray-50 align-top border-b border-gray-200">
                      {formatDateDisplay(date)}
                    </td>
                  )}
                  <td className="p-4 font-semibold text-primary-600 border-b border-gray-200">
                    {event}
                  </td>
                  {positions.map(position => (
                    <td key={`${date}-${event}-${position}`} className="p-2 border-b border-gray-200">
                      <select
                        value={assignments[position] || 'Unassigned'}
                        onChange={(e) => handleCellEdit(`${date} ${event}`, position, e.target.value)}
                        className="w-full px-1 py-1 text-sm border border-gray-300 rounded bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        style={{ minWidth: '85px', maxWidth: '110px' }}
                      >
                        <option value="Unassigned">Unassigned</option>
                        {getQualifiedMembers(position).map(member => (
                          <option key={member.id} value={member.name}>{member.name}</option>
                        ))}
                      </select>
                    </td>
                  ))}
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>

      {/* Action Controls */}
      <div className="space-y-4">
        {/* First row of buttons */}
        <div className="flex flex-wrap gap-4 justify-between">
          <button 
            onClick={toggleAssignmentCounts} 
            className="px-6 py-2 bg-cyan-500 text-white rounded font-semibold hover:bg-cyan-600 transition-colors"
          >
            Count Assignments
          </button>
          <button 
            onClick={checkForConflicts} 
            className="px-6 py-2 bg-orange-500 text-white rounded font-semibold hover:bg-orange-600 transition-colors"
          >
            Check Conflicts
          </button>
          <button 
            onClick={handleRegenerate} 
            className="px-6 py-2 bg-yellow-500 text-black rounded font-semibold hover:bg-yellow-600 transition-colors"
          >
            Regenerate
          </button>
        </div>

        {/* Second row with input and final actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            placeholder="Enter rota name"
            value={rotaName}
            onChange={(e) => setRotaName(e.target.value)}
            className="form-input flex-1"
          />
          <div className="flex gap-4">
            <button 
              onClick={exportToCSV} 
              className="btn-secondary px-6 py-2"
            >
              Export to CSV
            </button>
            <button
              onClick={handleSaveRota}
              className={`btn-primary px-6 py-2 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Rota'}
            </button>
          </div>
        </div>
      </div>

      {/* Assignment Counts Modal */}
      {showAssignmentCounts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Assignment Counts</h2>
            <ul className="space-y-2 mb-6">
              {Object.entries(assignmentCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([name, count]) => (
                  <li key={name} className="flex justify-between py-1">
                    <span className="text-gray-700">{name}:</span>
                    <span className="font-semibold text-gray-900">{count}</span>
                  </li>
                ))}
            </ul>
            <button 
              onClick={toggleAssignmentCounts}
              className="btn-primary w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Conflicts Modal */}
      {showConflicts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Conflicts Found</h2>
            {conflicts.length === 0 ? (
              <p className="text-green-600 mb-6">No conflicts found in the rota.</p>
            ) : (
              <ul className="space-y-2 mb-6">
                {conflicts.map((conflict, index) => (
                  <li key={index} className="text-red-600">
                    <span className="font-semibold">{conflict.event}:</span> {conflict.names.join(', ')}
                  </li>
                ))}
              </ul>
            )}
            <button 
              onClick={() => setShowConflicts(false)}
              className="btn-primary w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default GeneratedRotaPage;